import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {Inject, Injectable, LOCALE_ID, TemplateRef} from '@angular/core';
import {map} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {MatSnackBar} from '@angular/material';
import {environment} from '../../../environments/environment';
import {RoleService} from './role-service';
import data from "../../../assets/data.json";
import {ErrorMessageComponent} from '../_helpers/components/error-message.component';
import {ResultMessageComponent} from "../_helpers/components/result-message.component";
import {ImageViewerComponent} from "../_helpers/components/image-viewer.component";
import {formatDate, Location} from "@angular/common";
import {ContrackConstants} from "../_helpers/other/contrack-constants";

@Injectable()
export class HttpService extends RoleService {

  private AUTH_URL = environment.AUTH_URL;
  private BASE_URL = environment.BASE_URL;
  private _jsonData: any;

  constructor(private http: HttpClient, private router: Router, private _snackBar: MatSnackBar,
              @Inject(LOCALE_ID) private locale: string) {
    super();
    this.setJsonData = data;
  }

  get getJsonData(): any {
    return this._jsonData;
  }

  set setJsonData(value: any) {
    this._jsonData = value;
  }

  get(url) {
    return  this.http.get<any>(this.BASE_URL + url);
  }

  post(url, data) {
    return  this.http.post<any>(this.BASE_URL + url, data);
  }

  put(url, data) {
    return  this.http.put<any>(this.BASE_URL + url, data);
  }

  delete(url) {
    return  this.http.delete<any>(this.BASE_URL + url);
  }

  login(url, data) {
    return this.http.post(this.AUTH_URL + 'signin', data).pipe(map((result: any) => {
      sessionStorage.clear();
      if (result.accessToken) {
        sessionStorage.setItem('auth', result.hasOwnProperty('tokenType') ? result.tokenType + ' ' + result.accessToken : null);
        sessionStorage.setItem('token', result.hasOwnProperty('accessToken') ? result.accessToken : null);
        sessionStorage.setItem('name', result.hasOwnProperty('name') ? result.name : 'USER');
        sessionStorage.setItem('customerName', result.hasOwnProperty('customerName') ? result.customerName : null);
        sessionStorage.setItem('customerId', result.hasOwnProperty('customerId') ? result.customerId : null);
        sessionStorage.setItem('userId', result.hasOwnProperty('userId') ? result.userId : '-1');
        sessionStorage.setItem('roles', result.hasOwnProperty('roles') ? JSON.stringify(result.roles) : null);
		sessionStorage.setItem('vendorId', result.hasOwnProperty('vendorId') ? JSON.stringify(result.vendorId) : null);
		sessionStorage.setItem('vendorAdminId', result.hasOwnProperty('vendorAdminId') ? JSON.stringify(result.vendorAdminId) : null);
        return true;
      }
      return false;
    }));
  }

  logout() {
    sessionStorage.clear();
    this.router.navigate(['/authentication']).catch( reason => {
      console.log(reason);
    });
  }

  onErrorShow(error: any) {
    console.log(error);
    let errorMessage = 'Bad Request';
    if(error.error.hasOwnProperty('errors') && error.error.errors[0].hasOwnProperty('defaultMessage')) {
      this.showApiError(error.error.errors);
      errorMessage = error.error.errors[0].defaultMessage;
      return;
    } else if(error.error.hasOwnProperty('error') && error.error.hasOwnProperty('status')) {
      if(error.error.hasOwnProperty('message') && error.error.message !== '') {
        this.showApiError([{defaultMessage: error.error.message, isNotFormError: true}]);
      } else {
        this.showSnackBar('error', error.error.error, error.error.status);
      }
      return;
    } else if(error.error.hasOwnProperty('success') && !error.error.success) {
      this.showSnackBar('error', error.error.message);
      return;
    } else {
      this.showSnackBar('error', errorMessage);
      return;
    }
  }

  showSnackBar(type: messageType, message: string, action?: string) {
    if(type === 'error') {
      this.showApiError([{defaultMessage: message, isNotFormError: true, isWarning: false}]);
    } else if(type === 'warning') {
      this.showApiError([{defaultMessage: message, isNotFormError: true, isWarning: true}]);
    } else {
      this.showApiResult(message);
    }
  }

  showApiError(errors?: any[] | string[] | string) {
    let panelClass: string[] = ['text-center', 'show_warning_message', 'margin_10_imp', 'error_snackbar'];
    if(errors.length === 1 && errors[0].isNotFormError === true) {
      panelClass.pop();
    }

    this._snackBar.openFromComponent(ErrorMessageComponent, {
      data: errors,
      duration: 10000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: panelClass
    });
  }

  showApiResult(result?: string) {
    this._snackBar.openFromComponent(ResultMessageComponent, {
      data: [{defaultMessage: result}],
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ['text-center', 'show_warning_message', 'margin_10_imp']
    });
  }

  viewImage(url: string | any) {
    return this._snackBar.openFromComponent(ImageViewerComponent, {
      data: url,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: ''
    });
  }

  showLoading(message?: string) {
    if (message === undefined) {
      message = 'Please Wait...';
    }
    Swal.fire({
      title: message,
      allowEscapeKey: false,
      allowEnterKey: false,
      allowOutsideClick: false
    });
    Swal.showLoading();
  }

  hideLoading(timeout?: number) {
    timeout = (timeout === undefined || timeout === null) ? 1000 : timeout;
    setTimeout(() => {
      Swal.clickCancel();
    }, timeout);
  }

  downloadFile(url: string) {
    return this.http.get(this.BASE_URL + url, {
      responseType: 'blob',
      headers: new HttpHeaders().append('Authorization', sessionStorage.getItem('auth'))
    });
  }

  generateFile(url: string) {
    return this.http.get(this.BASE_URL + url, HttpService.getInterceptorOptions('file'));
  }

  static getInterceptorOptions(req_type: string) {
    return  {
      headers: new HttpHeaders({
        'req_type':  req_type
      })
    };
  }

  parseDate(date: string|any, dateFormat?: string): string {
    if(dateFormat == undefined || dateFormat == null) {
      dateFormat = ContrackConstants.dateFormatForInput;
    }
    return formatDate(date, dateFormat, this.locale);
  }
}
export type messageType = 'success' | 'error' | 'warning';
